import {FC} from 'react'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useLocation, useNavigate} from 'react-router-dom'
import {checkIsActive, WithChildren} from '../../../../helpers'
import {useAuth} from '../../../../../app/modules/auth'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'

type Props = {
  to: string
  title: string
  fontIcon?: string
  hasBullet?: boolean
  badge?: React.ReactNode
  /** Abre em nova aba com elemento anchor em vez do Link do React Router */
  external?: boolean
}

const menu: {[key: string]: string} = {
  Sprints: 'gestao-de-sprints',
  Cronogramas: 'cronogramas',
  'Time Tracking': 'timetracking',
  Clientes: 'gestao-de-clientes',
  Contratos: 'gestao-de-contratos',
  Posts: 'wiki',
  Rascunhos: 'wiki',
  Categorias: 'wiki',
  Tags: 'wiki',
  'Tickets do Portal do Cliente': 'tickets-do-portal-do-cliente',
  Tickets: 'tickets-do-portal-do-cliente',
  Acessos: 'tickets-do-portal-do-cliente',
}

// Ids usados como alvo do tour guiado (`modules/tutorial/tutorialSteps.ts`). Título fora do mapa
// não recebe id (antes gerava `sidebar-undefined` repetido em vários itens).
const tutorial: {[key: string]: string} = {
  Dashboard: 'dashboard',
  'Minhas tarefas': 'minhas-tarefas',
  Calendário: 'calendario',
  Relatórios: 'relatorios',
  Projetos: 'projetos',
  Sprints: 'sprints',
  Cronogramas: 'cronogramas',
  'Time Tracking': 'timetracking',
  Clientes: 'clientes',
  Contratos: 'contratos',
}

const SidebarMenuItem: FC<Props & WithChildren> = ({
  children,
  to,
  title,
  fontIcon,
  hasBullet = false,
  badge,
  external = false,
}) => {
  const {currentUser} = useAuth()
  const {pathname} = useLocation()
  const navigate = useNavigate()
  const isActive = checkIsActive(pathname, to)

  // Garantir que a verificação de módulos sempre retorne um booleano explícito
  // Itens sem módulo de assinatura: sempre liberados (ex.: listagem global de tarefas da conta).
  const titlesWithoutModuleCheck = [
    'Dashboard',
    'Faturamento',
    'Equipes',
    'Usuários',
    'Relatórios',
    'Projetos',
    'Minhas tarefas',
    'Todas as tarefas',
    'Desempenho',
    'Meu desempenho',
  ]
  const hasModuleAccess =
    titlesWithoutModuleCheck.includes(title) ||
    (currentUser?.instance?.plan?.modules?.some(
      (module) => module.slug_name == menu[title] && module.pivot.active
    ) ??
      false)

  const linkClassName = clsx('menu-link without-sub', {active: isActive})

  const menuInner = (
    <>
      {hasBullet && (
        <span className='menu-bullet'>
          <span className='bullet bullet-dot'></span>
        </span>
      )}
      {fontIcon && (
        <span
          className='menu-icon position-relative'
          id={tutorial[title] ? 'sidebar-' + tutorial[title] : undefined}
        >
          <i className={clsx('bi', fontIcon)}></i>
          {title === 'Tickets do Portal do Cliente' &&
            (currentUser?.unread_client_portal_tickets_count ?? 0) > 0 && (
              <span className='tr-sidebar-unread-dot bullet bullet-dot bg-danger h-6px w-6px position-absolute animation-blink' />
            )}
        </span>
      )}
      <span className='menu-title'>
        {title}
        {badge}
      </span>
    </>
  )

  return (
    <div className='menu-item'>
      {external ? (
        <a className={linkClassName} href={to} target='_blank' rel='noopener noreferrer'>
          {menuInner}
        </a>
      ) : title === 'Calendário' || hasModuleAccess ? (
        <Link className={linkClassName} to={to}>
          {menuInner}
        </Link>
      ) : (
        <OverlayTrigger
          placement='top'
          delay={{show: 50, hide: 100}}
          overlay={
            <Tooltip>
              Este recurso está disponível apenas em planos superiores. Atualize seu plano para
              acessá-lo.
            </Tooltip>
          }
        >
          <div
            className='menu-link without-sub disabled'
            style={{cursor: currentUser?.administrator ? 'pointer' : 'not-allowed'}}
            role={currentUser?.administrator ? 'button' : undefined}
            tabIndex={currentUser?.administrator ? 0 : -1}
            onClick={() => {
              if (currentUser?.administrator) {
                navigate('/billings')
              }
            }}
            onKeyDown={(event) => {
              if (!currentUser?.administrator) return
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                navigate('/billings')
              }
            }}
          >
            {hasBullet && (
              <span className='menu-bullet'>
                <span className='bullet bullet-dot'></span>
              </span>
            )}
            {fontIcon && (
              <span
                className='menu-icon'
                id={tutorial[title] ? 'sidebar-' + tutorial[title] : undefined}
              >
                <i className={clsx('bi', fontIcon)}></i>
              </span>
            )}
            <span className='menu-title'>
              {title}
              {badge}
            </span>
            <i className='fa fa-lock'></i>
          </div>
        </OverlayTrigger>
      )}

      {children}
    </div>
  )
}

export {SidebarMenuItem}
